import { sideTone, sportsBuyOption } from '@/lib/sports-market';
import type { OrderSide } from '@/analytics';
import type { MarketOptionDto, SportsMoneylineDto } from '@/lib/models';
import { MarketOutcomePricesSubscriber, useOutcomePrices } from '../BinaryMarkets/useOutcomePrices';
import { SportsBlockHeader } from './SportsBlockHeader';
import { SportsOutcomePill } from './SportsOutcomePill';

export interface SportsMoneylineBlockProps {
  /** The event's moneyline block (2-way or 3-way). */
  moneyline: SportsMoneylineDto;
  /** Opens the buy-in popup pre-set to the tapped answer (ARC-80 §7). */
  onOpenBuy: (option: MarketOptionDto, side: OrderSide) => void;
}

/**
 * The Moneyline section of the sports event page: a title + volume and the 2-way
 * (or 3-way, incl. Draw) team pills. Live prices overlay the seed via Centrifugo;
 * tapping a pill opens the inherited buy-in popup.
 */
export function SportsMoneylineBlock({ moneyline, onOpenBuy }: SportsMoneylineBlockProps) {
  const { prices, setPrice } = useOutcomePrices();

  return (
    <div className="flex flex-col gap-2">
      <MarketOutcomePricesSubscriber
        marketId={moneyline.marketId}
        externalMarketId={moneyline.externalMarketId}
        onPrice={setPrice}
      />

      <SportsBlockHeader title="Moneyline" volume={moneyline.volume} />

      <div className={moneyline.outcomes.length === 3 ? 'grid grid-cols-3 gap-2' : 'grid grid-cols-2 gap-2'}>
        {moneyline.outcomes.map((o) => (
          <SportsOutcomePill
            key={o.outcomeId}
            tone={sideTone(o.side)}
            label={o.shortName || o.label}
            percent={prices[o.externalOutcomeId]?.yesCents ?? o.chancePercent}
            onClick={() => onOpenBuy(sportsBuyOption(o, moneyline), 'yes')}
          />
        ))}
      </div>
    </div>
  );
}
